/**
 * NAZARE Tea Collection — Cart Drawer
 * Handles open/close, add to cart, quantity updates, free shipping bar
 */

(function () {
  'use strict';

  // Single source of truth: read the threshold (in dollars) injected from
  // settings.free_shipping_threshold via window.theme.freeShippingThreshold
  // (set by the cart-drawer snippet). Falls back to 75 if not present.
  const FREE_SHIPPING_THRESHOLD =
    (window.theme && typeof window.theme.freeShippingThreshold === 'number')
      ? window.theme.freeShippingThreshold
      : 75; // dollars

  // ─── Routes (single source of truth from window.routes) ───────────────────
  const ROUTES = window.routes || {};
  const CART_URL = ROUTES.cart_url || '/cart';
  const CART_ADD_URL = ROUTES.cart_add_url || '/cart/add';
  const CART_CHANGE_URL = ROUTES.cart_change_url || '/cart/change';

  // ─── DOM refs ─────────────────────────────────────────────────────────────
  const drawer = document.querySelector('.cart-drawer');
  const overlay = document.querySelector('.cart-drawer__overlay');
  const closeBtn = document.querySelector('.cart-drawer__close');
  const body = document.body;

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    body.classList.add('no-scroll');
    drawer.setAttribute('aria-hidden', 'false');
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    body.classList.remove('no-scroll');
    drawer.setAttribute('aria-hidden', 'true');
  }

  // ─── Toggle triggers ──────────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle) {
      e.preventDefault();
      if (drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      } else {
        refreshCart().then(openDrawer);
      }
    }
  });

  if (overlay) overlay.addEventListener('click', closeDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // ─── Add to cart ──────────────────────────────────────────────────────────
  window.addToCart = async function (variantId, quantity = 1) {
    try {
      const res = await fetch(CART_ADD_URL + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });

      if (!res.ok) {
        const err = await res.json();
        showCartError(err.description || window.theme?.strings?.cart?.error || 'Could not add item.');
        return null;
      }

      const data = await res.json();
      await refreshCart();
      openDrawer();
      return data;
    } catch (err) {
      console.error('[cart] addToCart error:', err);
      showCartError('Something went wrong. Please try again.');
      return null;
    }
  };

  // ─── Quantity controls ────────────────────────────────────────────────────
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-drawer__quantity [data-action]');
    if (!btn) return;

    const lineItem = btn.closest('[data-line-key]');
    if (!lineItem) return;

    const key = lineItem.dataset.lineKey;
    const currentQty = parseInt(lineItem.querySelector('.cart-drawer__qty-display')?.textContent || '1', 10);
    const action = btn.dataset.action;
    const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

    await updateCartItem(key, newQty);
  });

  async function updateCartItem(key, quantity) {
    try {
      const res = await fetch(CART_CHANGE_URL + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });

      if (!res.ok) throw new Error('Cart update failed');
      await refreshCart();
    } catch (err) {
      console.error('[cart] updateCartItem error:', err);
    }
  }

  // ─── Remove item ──────────────────────────────────────────────────────────
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-remove-item]');
    if (!removeBtn) return;
    const key = removeBtn.dataset.removeItem;
    await updateCartItem(key, 0);
  });

  // ─── Refresh cart ─────────────────────────────────────────────────────────
  async function refreshCart() {
    try {
      const res = await fetch(CART_URL + '.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();

      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
      updateFreeShippingBar(cart.total_price);

      return cart;
    } catch (err) {
      console.error('[cart] refreshCart error:', err);
    }
  }

  // ─── Render cart HTML ─────────────────────────────────────────────────────
  function renderCartDrawer(cart) {
    const itemsContainer = document.querySelector('.cart-drawer__items');
    const subtotalEl = document.querySelector('.cart-drawer__subtotal-price');
    const emptyEl = document.querySelector('.cart-drawer__empty');
    const footerEl = document.querySelector('.cart-drawer__footer');

    if (!itemsContainer) return;

    if (cart.item_count === 0) {
      itemsContainer.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (footerEl) footerEl.style.display = '';

    itemsContainer.innerHTML = cart.items.map((item) => buildCartItemHTML(item)).join('');

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }
  }

  function buildCartItemHTML(item) {
    const imgSrc = item.image
      ? item.image.replace(/(\.[a-z]+)$/, '_120x120$1')
      : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

    return `
      <div class="cart-drawer__item" data-line-key="${item.key}">
        <a class="cart-drawer__item-img" href="${item.url}">
          <img src="${imgSrc}" alt="${item.title}" width="80" height="80" loading="lazy">
        </a>
        <div class="cart-drawer__item-info">
          <a class="cart-drawer__item-title" href="${item.url}">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>`
            : ''}
          <div class="cart-drawer__item-bottom">
            <div class="cart-drawer__quantity">
              <button class="cart-drawer__qty-btn" data-action="decrease" aria-label="Decrease quantity">−</button>
              <span class="cart-drawer__qty-display">${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-action="increase" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
        <button class="cart-drawer__remove" data-remove-item="${item.key}" aria-label="Remove item">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
  }

  // ─── Update header count badge ────────────────────────────────────────────
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ─── Free shipping bar ────────────────────────────────────────────────────
  function updateFreeShippingBar(totalCents) {
    const bar = document.querySelector('.cart-drawer__shipping-bar-fill');
    const text = document.querySelector('.cart-drawer__shipping-text');
    if (!bar && !text) return;

    const totalDollars = totalCents / 100;
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - totalDollars);
    const pct = Math.min(100, (totalDollars / FREE_SHIPPING_THRESHOLD) * 100);

    if (bar) bar.style.width = pct + '%';

    if (text) {
      if (remaining <= 0) {
        text.textContent = "You've unlocked free shipping! 🎉";
        text.classList.add('is-unlocked');
      } else {
        text.textContent = `You're ${formatMoney(remaining * 100)} away from free shipping!`;
        text.classList.remove('is-unlocked');
      }
    }
  }

  // ─── Error message ────────────────────────────────────────────────────────
  function showCartError(message) {
    let errorEl = document.querySelector('.cart-drawer__error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'cart-drawer__error';
      const header = document.querySelector('.cart-drawer__header');
      if (header) header.after(errorEl);
    }
    errorEl.textContent = message;
    errorEl.style.display = '';
    setTimeout(() => { errorEl.style.display = 'none'; }, 4000);
  }

  // ─── Init: refresh count on page load ─────────────────────────────────────
  fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      updateCartCount(cart.item_count);
      updateFreeShippingBar(cart.total_price);
    })
    .catch(() => {});

})();
